import { useState, useEffect } from 'react';
import { X, Moon, Sun, Bell, BellOff, Shield, Globe, Trash2, LogOut, MessageCircle } from 'lucide-react';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
}

export const SettingsModal = ({ isOpen, onClose, onLogout }: SettingsModalProps) => {
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [notifications, setNotifications] = useState(true);
  const [language, setLanguage] = useState('id');
  const [telegramUser, setTelegramUser] = useState<any>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const savedTheme = localStorage.getItem('gotradingzy_theme') || 'dark';
      setTheme(savedTheme as 'dark' | 'light');
      const savedNotif = localStorage.getItem('gotradingzy_notifications');
      setNotifications(savedNotif !== 'false');
      const savedLang = localStorage.getItem('gotradingzy_language') || 'id';
      setLanguage(savedLang);
      const tgUser = localStorage.getItem('gotradingzy_telegram_user');
      if (tgUser) setTelegramUser(JSON.parse(tgUser));
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleThemeChange = (newTheme: 'dark' | 'light') => {
    setTheme(newTheme);
    localStorage.setItem('gotradingzy_theme', newTheme);
    document.documentElement.classList.toggle('light', newTheme === 'light');
  };

  const handleNotificationChange = () => {
    const newValue = !notifications;
    setNotifications(newValue);
    localStorage.setItem('gotradingzy_notifications', String(newValue));
  };

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
    localStorage.setItem('gotradingzy_language', lang);
  };

  const handleClearData = () => {
    if (confirm('Apakah Anda yakin ingin menghapus semua data lokal?')) {
      localStorage.removeItem('gotradingzy_telegram_user');
      localStorage.removeItem('gotradingzy_theme');
      localStorage.removeItem('gotradingzy_notifications');
      localStorage.removeItem('gotradingzy_language');
      window.location.reload();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('gotradingzy_telegram_user');
    onLogout();
    onClose();
    window.location.reload();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4">
      <div className="bg-[#0a0e17] border border-slate-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#0a0e17] p-6 border-b border-slate-800 z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">Pengaturan</h2>
            <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {telegramUser && (
            <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-[#0088cc] rounded-full flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white">
                    @{telegramUser.telegram_username || 'User'}
                  </h3>
                  <p className="text-sm text-slate-400">
                    Terhubung via Telegram
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Tampilan</h3>

            <div className="p-4 bg-slate-800/30 rounded-xl space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {theme === 'dark' ? (
                    <Moon className="w-5 h-5 text-slate-400" />
                  ) : (
                    <Sun className="w-5 h-5 text-amber-400" />
                  )}
                  <span className="text-white">Tema</span>
                </div>
                <div className="flex bg-slate-800 rounded-lg p-1">
                  <button
                    onClick={() => handleThemeChange('dark')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      theme === 'dark' ? 'bg-emerald-500 text-white' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    Gelap
                  </button>
                  <button
                    onClick={() => handleThemeChange('light')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      theme === 'light' ? 'bg-emerald-500 text-white' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    Terang
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Globe className="w-5 h-5 text-slate-400" />
                  <span className="text-white">Bahasa</span>
                </div>
                <select
                  value={language}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  className="bg-slate-800 text-white px-3 py-1.5 rounded-lg border border-slate-700 text-sm"
                >
                  <option value="id">Indonesia</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Notifikasi</h3>

            <div className="p-4 bg-slate-800/30 rounded-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {notifications ? (
                    <Bell className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <BellOff className="w-5 h-5 text-slate-400" />
                  )}
                  <div>
                    <span className="text-white">Notifikasi Browser</span>
                    <p className="text-xs text-slate-500">Terima alert harga dan berita</p>
                  </div>
                </div>
                <button
                  onClick={handleNotificationChange}
                  className={`w-12 h-6 rounded-full transition-colors relative ${
                    notifications ? 'bg-emerald-500' : 'bg-slate-700'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      notifications ? 'left-7' : 'left-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Keamanan & Data</h3>

            <div className="p-4 bg-slate-800/30 rounded-xl space-y-3">
              <div className="flex items-center space-x-3 text-slate-400">
                <Shield className="w-5 h-5" />
                <span className="text-sm">Login diproteksi dengan OTP Telegram</span>
              </div>
            </div>
          </div>

          <div className="space-y-3 pt-4 border-t border-slate-800">
            <button
              onClick={handleClearData}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-slate-800/50 text-slate-400 hover:text-white rounded-xl transition-colors"
            >
              <Trash2 className="w-5 h-5" />
              <span>Hapus Data Lokal</span>
            </button>

            {telegramUser && (
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-xl transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span>Keluar</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
